import { useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { centavosDe, formatarBytes, formatarCentavos, formatarCpf, formatarCpfOuEmail, soDigitos } from "../formatos";
import { IconeArquivo, IconeBaixo, IconeOlho, IconeOlhoFechado } from "../icones";
import { BUSCA_A_PARTIR_DE, ListaComBusca } from "./ListaComBusca";

type Base = Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "prefix"> & {
  rotulo: string;
  dica?: ReactNode;
  erro?: string;
};

/** `contador` (Material 3): "12/500" no fim da linha de apoio, ao lado da dica ou do erro. */
function Moldura({ id, rotulo, dica, erro, contador, children }: { id: string; rotulo: string; dica?: ReactNode; erro?: string; contador?: string; children: ReactNode }) {
  const apoio = erro ? <span className="mtc-campo__erro" id={`${id}-erro`} role="alert">{erro}</span> : dica ? <span className="mtc-campo__dica" id={`${id}-dica`}>{dica}</span> : null;
  return (
    <div className={`mtc-campo${erro ? " mtc-campo--erro" : ""}`}>
      <label className="mtc-campo__rotulo" htmlFor={id}>{rotulo}</label>
      {children}
      {contador ? (
        <div className="mtc-campo__apoio">
          {apoio}
          <span className="mtc-campo__contador" data-contador>{contador}</span>
        </div>
      ) : apoio}
    </div>
  );
}

const descrito = (id: string, erro?: string, dica?: ReactNode) => (erro ? `${id}-erro` : dica ? `${id}-dica` : undefined);

/**
 * Campo de texto padrao. `mono` pra dado tecnico (ID de transacao, final de cartao). `contador` com `maxLength` mostra
 * quanto ja foi escrito do limite ("12/25"), como no Material 3 (nome do favorecido do PIX, cidade).
 */
export function Campo({ rotulo, dica, erro, valor, aoMudar, mono, prefixo, contador, id: idProp, className, ...p }: Base & { valor: string; aoMudar: (v: string) => void; mono?: boolean; prefixo?: string; contador?: boolean }) {
  const idAuto = useId();
  const id = idProp ?? idAuto;
  const classes = ["mtc-campo__entrada", mono && "mtc-campo__entrada--mono", prefixo && "mtc-campo__entrada--com-prefixo", className].filter(Boolean).join(" ");
  return (
    <Moldura id={id} rotulo={rotulo} dica={dica} erro={erro} contador={contador && p.maxLength ? `${valor.length}/${p.maxLength}` : undefined}>
      <div className="mtc-campo__caixa">
        {prefixo && <span className="mtc-campo__prefixo" aria-hidden>{prefixo}</span>}
        <input id={id} className={classes} value={valor} onChange={(e) => aoMudar(e.target.value)} aria-invalid={erro ? true : undefined} aria-describedby={descrito(id, erro, dica)} {...p} />
      </div>
    </Moldura>
  );
}

/** Senha SEMPRE com mostrar/ocultar (regra do design system): quem digita precisa conferir o que digitou. */
export function CampoSenha({ rotulo = "Senha", dica, erro, valor, aoMudar, id: idProp, autoComplete = "current-password", ...p }: Omit<Base, "rotulo"> & { rotulo?: string; valor: string; aoMudar: (v: string) => void }) {
  const idAuto = useId();
  const id = idProp ?? idAuto;
  const [visivel, setVisivel] = useState(false);
  return (
    <Moldura id={id} rotulo={rotulo} dica={dica} erro={erro}>
      <div className="mtc-campo__caixa">
        <input id={id} type={visivel ? "text" : "password"} className="mtc-campo__entrada mtc-campo__entrada--com-acao" value={valor} onChange={(e) => aoMudar(e.target.value)} autoComplete={autoComplete} aria-invalid={erro ? true : undefined} aria-describedby={descrito(id, erro, dica)} spellCheck={false} {...p} />
        <button type="button" className="mtc-campo__acao" onClick={() => setVisivel((v) => !v)} aria-label={visivel ? "Ocultar senha" : "Mostrar senha"} aria-pressed={visivel} aria-controls={id}>
          {visivel ? <IconeOlhoFechado /> : <IconeOlho />}
        </button>
      </div>
    </Moldura>
  );
}

/** Codigo de 6 digitos. Um campo so (colar funciona), teclado numerico no celular, autocompletar do SMS/e-mail. */
export function CampoCodigo({ rotulo = "Código", dica, erro, valor, aoMudar, id: idProp, ...p }: Omit<Base, "rotulo"> & { rotulo?: string; valor: string; aoMudar: (v: string) => void }) {
  const idAuto = useId();
  const id = idProp ?? idAuto;
  return (
    <Moldura id={id} rotulo={rotulo} dica={dica} erro={erro}>
      <input id={id} className="mtc-campo__entrada mtc-campo__entrada--codigo" value={valor} onChange={(e) => aoMudar(soDigitos(e.target.value).slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" aria-invalid={erro ? true : undefined} aria-describedby={descrito(id, erro, dica)} {...p} />
    </Moldura>
  );
}

/** Login: aceita CPF (formata enquanto digita) ou e-mail. */
export function CampoCpfOuEmail({ rotulo = "CPF ou e-mail", valor, aoMudar, ...p }: Omit<Base, "rotulo"> & { rotulo?: string; valor: string; aoMudar: (v: string) => void }) {
  return <Campo rotulo={rotulo} valor={valor} aoMudar={(v) => aoMudar(formatarCpfOuEmail(v))} autoComplete="username" autoCapitalize="none" spellCheck={false} {...p} />;
}

export function CampoCpf({ rotulo = "CPF", valor, aoMudar, ...p }: Omit<Base, "rotulo"> & { rotulo?: string; valor: string; aoMudar: (v: string) => void }) {
  return <Campo rotulo={rotulo} valor={valor} aoMudar={(v) => aoMudar(formatarCpf(v))} inputMode="numeric" placeholder="000.000.000-00" mono {...p} />;
}

/** Dinheiro em reais; o valor e em centavos (inteiro), igual ao backend. */
/**
 * `moeda`: o prefixo do campo. Real e o padrao; dolar existe porque a MTC Global anuncia em dolar e cobra em real
 * (spec 020), e quem digita o preco precisa ver em qual moeda esta digitando. O componente nao converte nada.
 */
export function CampoDinheiro({ rotulo, centavos, aoMudar, moeda = "BRL", ...p }: Omit<Base, "rotulo"> & { rotulo: string; centavos: number; aoMudar: (centavos: number) => void; moeda?: "BRL" | "USD" }) {
  return <Campo rotulo={rotulo} valor={centavos ? formatarCentavos(centavos) : ""} aoMudar={(v) => aoMudar(centavosDe(v))} inputMode="numeric" prefixo={moeda === "USD" ? "US$" : "R$"} placeholder="0,00" mono {...p} />;
}

/** Dia (AAAA-MM-DD) com o seletor do proprio navegador. Que dia vale (util, de hoje em diante) quem diz e o backend. */
export function CampoData({ rotulo, valor, aoMudar, ...p }: Base & { valor: string; aoMudar: (v: string) => void }) {
  return <Campo rotulo={rotulo} valor={valor} aoMudar={aoMudar} type="date" mono {...p} />;
}

/** Dia e hora (AAAA-MM-DDTHH:MM, sem fuso) com o seletor do navegador. Quem usa diz de que fuso e e converte. */
export function CampoDataHora({ rotulo, valor, aoMudar, ...p }: Base & { valor: string; aoMudar: (v: string) => void }) {
  return <Campo rotulo={rotulo} valor={valor} aoMudar={aoMudar} type="datetime-local" mono {...p} />;
}

/** Hora (HH:MM) com o seletor do navegador. */
export function CampoHora({ rotulo, valor, aoMudar, ...p }: Base & { valor: string; aoMudar: (v: string) => void }) {
  return <Campo rotulo={rotulo} valor={valor} aoMudar={aoMudar} type="time" mono {...p} />;
}

export type OpcaoDeSelecao<T extends string> = { valor: T; rotulo: string; grupo?: string };

/**
 * Lista de escolha. Ate 7 opcoes, select nativo: no celular abre o seletor do sistema. Com mais, a lista ganha busca
 * dentro dela (spec 059, bloco 2: `ListaComBusca`). `grupo` junta as opcoes em blocos nas duas. `busca={false}` mantem
 * o nativo em qualquer tamanho: a area do cliente e o checkout sao de celular, onde o seletor do sistema e o melhor.
 */
export function CampoSelecao<T extends string>({ rotulo, dica, erro, valor, aoMudar, opcoes, vazio, id: idProp, disabled, required, busca = true }: { rotulo: string; dica?: ReactNode; erro?: string; valor: T | ""; aoMudar: (v: T) => void; opcoes: OpcaoDeSelecao<T>[]; vazio?: string; id?: string; disabled?: boolean; required?: boolean; busca?: boolean }) {
  const idAuto = useId();
  const id = idProp ?? idAuto;
  if (busca && opcoes.length >= BUSCA_A_PARTIR_DE) {
    return (
      <Moldura id={id} rotulo={rotulo} dica={dica} erro={erro}>
        <div className="mtc-campo__caixa">
          <ListaComBusca id={id} rotulo={rotulo} valor={valor} aoMudar={aoMudar} opcoes={opcoes} vazio={vazio} obrigatorio={required} disabled={disabled} invalido={Boolean(erro)} descritoPor={descrito(id, erro, dica)} classe="mtc-campo__entrada mtc-campo__entrada--selecao" />
        </div>
      </Moldura>
    );
  }
  const grupos = [...new Set(opcoes.map((o) => o.grupo ?? ""))];
  const item = (o: OpcaoDeSelecao<T>) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>;
  return (
    <Moldura id={id} rotulo={rotulo} dica={dica} erro={erro}>
      <div className="mtc-campo__caixa">
        <select id={id} className="mtc-campo__entrada mtc-campo__entrada--selecao" value={valor} onChange={(e) => aoMudar(e.target.value as T)} disabled={disabled} required={required} aria-invalid={erro ? true : undefined} aria-describedby={descrito(id, erro, dica)}>
          {vazio !== undefined && <option value="" disabled={required}>{vazio}</option>}
          {grupos.length > 1 || grupos[0] ? grupos.map((g) => <optgroup key={g} label={g}>{opcoes.filter((o) => (o.grupo ?? "") === g).map(item)}</optgroup>) : opcoes.map(item)}
        </select>
        <IconeBaixo className="mtc-campo__seta" tamanho={16} />
      </div>
    </Moldura>
  );
}

/**
 * Texto de varias linhas (mensagem pra equipe, observacao pro cliente, motivo). `contador` com `maxLength` mostra quanto
 * ja foi escrito do limite ("12/500"), como no Material 3.
 */
export function CampoTextoLongo({ rotulo, dica, erro, valor, aoMudar, linhas = 3, contador, id: idProp, ...p }: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value"> & { rotulo: string; dica?: ReactNode; erro?: string; valor: string; aoMudar: (v: string) => void; linhas?: number; contador?: boolean }) {
  const idAuto = useId();
  const id = idProp ?? idAuto;
  return (
    <Moldura id={id} rotulo={rotulo} dica={dica} erro={erro} contador={contador && p.maxLength ? `${valor.length}/${p.maxLength}` : undefined}>
      <textarea id={id} className="mtc-campo__entrada mtc-campo__entrada--longo" rows={linhas} value={valor} onChange={(e) => aoMudar(e.target.value)} aria-invalid={erro ? true : undefined} aria-describedby={descrito(id, erro, dica)} {...p} />
    </Moldura>
  );
}

/** Ate aqui o arquivo escolhido e copiado pra memoria; maior que isso nenhum envio aceita, a validacao recusa. */
const ARQUIVO_NA_MEMORIA_ATE = 25 * 1024 * 1024;

/** Recusa do `preparar` com a frase pra pessoa (ex.: foto num formato que o aparelho nao abre). */
export class ArquivoRecusado extends Error {}

/** O que deu errado na escolha, pro app registrar (sem o nome do arquivo, que pode ter dado pessoal). */
export type FalhaDoArquivo = { etapa: "leitura" | "preparo"; motivo: string; tipo: string; tamanho: number };

/**
 * Arquivo (foto ou PDF de comprovante). O input nativo cobre a area inteira: tocar, arrastar e teclado funcionam
 * sem codigo extra, e no celular a camera aparece como opcao quando `aceita` inclui imagem. `preparar`: troca o arquivo
 * escolhido antes de entregar (o comprovante converte e reduz a foto); recusa com `ArquivoRecusado`. `aoFalhar`: a
 * leitura ou o preparo falhou (a tela ja avisou a pessoa).
 */
export function CampoArquivo({ rotulo, dica, erro, arquivo, aoEscolher, aceita, id: idProp, disabled, preparar, aoFalhar }: { rotulo: string; dica?: ReactNode; erro?: string; arquivo: File | null; aoEscolher: (f: File | null) => void; aceita?: string; id?: string; disabled?: boolean; preparar?: (f: File) => Promise<File>; aoFalhar?: (falha: FalhaDoArquivo) => void }) {
  const idAuto = useId();
  const id = idProp ?? idAuto;
  const ref = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [recusa, setRecusa] = useState<string>();
  const escolha = useRef(0);
  useEffect(() => { if (!arquivo && !preparando && ref.current) ref.current.value = ""; }, [arquivo, preparando]);
  // O arquivo vai pra memoria na hora da escolha. No Chrome do Android, foto escolhida pelo seletor de fotos ou da nuvem
  // pode ficar ilegivel ate o envio, e o envio morre no navegador sem chegar na API ("nao foi possivel falar com o
  // servidor"; 7 tentativas de um cliente em 28/10). Lendo aqui, ou ele vai inteiro ou a pessoa sabe na hora o que fazer.
  const escolher = async (f: File | null) => {
    const minha = ++escolha.current;
    setRecusa(undefined);
    if (!f || f.size > ARQUIVO_NA_MEMORIA_ATE) return aoEscolher(f);
    aoEscolher(null);
    setPreparando(true);
    let etapa: FalhaDoArquivo["etapa"] = "leitura";
    try {
      let pronto = new File([await f.arrayBuffer()], f.name, { type: f.type, lastModified: f.lastModified });
      etapa = "preparo";
      if (preparar) pronto = await preparar(pronto);
      if (minha === escolha.current) aoEscolher(pronto);
    } catch (e) {
      if (minha !== escolha.current) return;
      setRecusa(e instanceof ArquivoRecusado ? e.message : etapa === "leitura" ? "Não consegui abrir esse arquivo no seu aparelho. Salve o comprovante no celular (ou tire um print da tela) e escolha de novo." : "Não consegui preparar esse arquivo. Tire um print da tela do comprovante e envie o print.");
      aoFalhar?.({ etapa, motivo: e instanceof Error ? `${e.name}: ${e.message}`.slice(0, 200) : String(e).slice(0, 200), tipo: f.type || "(sem tipo)", tamanho: f.size });
    } finally {
      if (minha === escolha.current) setPreparando(false);
    }
  };
  return (
    <Moldura id={id} rotulo={rotulo} dica={dica} erro={recusa ?? erro}>
      <div className="mtc-arquivo" data-arrastando={arrastando || undefined} data-escolhido={arquivo ? true : undefined} data-desligado={disabled || undefined} aria-busy={preparando || undefined}>
        <IconeArquivo tamanho={22} />
        {preparando ? (
          <>
            <strong>Preparando o arquivo</strong>
            <span className="mtc-arquivo__meta">Só um instante.</span>
          </>
        ) : arquivo ? (
          <>
            <strong className="mtc-arquivo__nome">{arquivo.name}</strong>
            <span className="mtc-arquivo__meta">{formatarBytes(arquivo.size)}. Clique ou toque para trocar.</span>
          </>
        ) : (
          <>
            <strong>Escolher arquivo</strong>
            <span className="mtc-arquivo__meta">Clique, toque ou arraste para cá</span>
          </>
        )}
        <input ref={ref} id={id} type="file" accept={aceita} disabled={disabled} onChange={(e) => void escolher(e.target.files?.[0] ?? null)} onDragEnter={() => setArrastando(true)} onDragLeave={() => setArrastando(false)} onDrop={() => setArrastando(false)} aria-invalid={recusa ?? erro ? true : undefined} aria-describedby={descrito(id, recusa ?? erro, dica)} />
      </div>
    </Moldura>
  );
}

/** Caixa de marcar com o texto ao lado (aceite de termos, confirmacoes). O texto pode ter link. */
export function CampoCaixa({ marcado, aoMudar, children, disabled, id }: { marcado: boolean; aoMudar: (v: boolean) => void; children: ReactNode; disabled?: boolean; id?: string }) {
  return (
    <label className="mtc-caixa">
      <input id={id} type="checkbox" checked={marcado} onChange={(e) => aoMudar(e.target.checked)} disabled={disabled} />
      <span>{children}</span>
    </label>
  );
}

/**
 * Liga e desliga (switch do Material 3), pra opcao que vale na hora e ja nasce com um padrao (ex.: "Gerar lembrete
 * pos-repasse", ligado). Checkbox nativo com `role="switch"`: clique, espaco e leitor de tela ("ligado") sem codigo extra.
 */
export function Interruptor({ rotulo, dica, ligado, aoMudar, disabled, id: idProp }: { rotulo: string; dica?: ReactNode; ligado: boolean; aoMudar: (v: boolean) => void; disabled?: boolean; id?: string }) {
  const idAuto = useId();
  const id = idProp ?? idAuto;
  return (
    <label className="mtc-interruptor" htmlFor={id}>
      <span className="mtc-interruptor__trilho">
        <input id={id} type="checkbox" role="switch" checked={ligado} onChange={(e) => aoMudar(e.target.checked)} disabled={disabled} aria-describedby={dica ? `${id}-dica` : undefined} />
        <span className="mtc-interruptor__pino" aria-hidden />
      </span>
      <span className="mtc-interruptor__texto">
        <span>{rotulo}</span>
        {dica && <span className="mtc-campo__dica" id={`${id}-dica`}>{dica}</span>}
      </span>
    </label>
  );
}
